/** @jsxImportSource @emotion/react */

import {
  Button,
  Checkbox,
  createStyles,
  Input,
  ListItem,
  Snackbar,
  Typography,
  useForm,
  useTheme,
  UtilityButton,
  Validators,
} from '@quickbit/qb-design-system';
import { useState } from 'react';
import { useAsync, useAsyncFn } from 'react-use';
import { QBLoader } from 'components';
import { useOrganisation } from 'contexts';
import { getKYB, getMerchant, submitAnswers } from './api';
import { ContactInformationForm } from './ContactInformationForm';
import { DateInput } from './DateInput';
import { FormData } from './types';
import { isValidLEI } from './utils';

const defaultForm: FormData = {
  incorporationDate: null,
  primaryAddress: {
    postalRegion: '',
    postalNumber: '',
    country: '',
    address1: '',
  },
  paymentReference: '',
  previousCompanyNames: '',
  leiNumber: '',
  usaConnection: false,
  businessOperations: '',
  reasonForUse: '',
  authorizedSignatory: '',
  iban: '',
  bic: '',
  relatedPeople: [],
};

const validators: Validators<FormData, FormData> = {
  authorizedSignatory: (value: string) =>
    value.length < 1 ? 'Can not be empty.' : undefined,
  leiNumber: (lei: string | null) => {
    if (lei === '' || lei === null) return undefined;
    return isValidLEI(lei) ? undefined : 'Invalid LEI number.';
  },
};

export const Kyb = () => {
  const styles = useStyles();
  const { spacing } = useTheme();
  const [disableForm, setDisableForm] = useState(false);
  const { fields, setters, errors } = useForm<FormData>(
    defaultForm,
    validators
  );

  const {
    selectedMerchant: { merchantId },
  } = useOrganisation();

  const [{ value, loading }, submit] = useAsyncFn(async () => {
    const response = await submitAnswers({ merchantId, ...fields });
    setDisableForm(true);
    return response;
  }, [errors, fields]);

  const { loading: kybLoading } = useAsync(async () => {
    const response = await getKYB(merchantId);
    await getMerchantData(response);
    setters.set(response);
  }, [merchantId]);

  const [, getMerchantData] = useAsyncFn(
    async (data: FormData) => {
      const response = await getMerchant(merchantId);
      setters.set({
        ...data,
        bic: response.paymentDetails.bic,
        iban: response.paymentDetails.iban,
        paymentReference: response.paymentDetails.reference,
      });
      setDisableForm(true);
    },
    [merchantId]
  );

  const autofill = () => {
    setters.previousCompanyNames?.set('Previous Company Name');
    setters.authorizedSignatory.set('John Doe');
    setters.incorporationDate.set(new Date('2020-01-01'));
    setters.primaryAddress.set({
      postalRegion: 'Stockholm',
      postalNumber: '114 40',
      country: 'SE',
      address1: 'Example Street 1',
    });
    setters.businessOperations.set(
      'This company is involved in software development and consulting.'
    );
    setters.paymentReference.set('Payment Reference Example');
    setters.leiNumber.set('5493001KJTIIGC8Y1R12');
    setters.iban.set('DE89370400440532013000');
    setters.bic.set('COBADEFFXXX');
  };

  if (kybLoading) {
    return <QBLoader />;
  }

  return (
    <div css={[styles.container]}>
      <div
        css={{
          flexGrow: 1,
          marginRight: spacing.xs,
        }}
      >
        <Typography variant="subtitle">Update Kyb</Typography>
        {process.env.REACT_APP_ENVIRONMENT !== 'production' && (
          <UtilityButton
            text="Autofill"
            variant="primary"
            onClick={autofill}
            disabled={disableForm}
          />
        )}
        <div
          css={{
            marginTop: spacing.xxs,
            display: 'flex',
            flexDirection: 'column',
            gap: spacing.xxs,
          }}
        >
          <Input
            label="Previous company name(s)"
            value={fields.previousCompanyNames ?? ''}
            onChange={(e) => setters.previousCompanyNames?.set(e.target.value)}
            disabled={disableForm}
          />
          <Input
            label="Company's authorized signatory"
            value={fields.authorizedSignatory}
            onChange={(e) => {
              setters.authorizedSignatory.set(e.target.value);
            }}
            error={!!errors.authorizedSignatory}
            helperText={errors.authorizedSignatory}
            disabled={disableForm}
          />
          <DateInput
            title="Incorporation date"
            value={fields.incorporationDate}
            onChange={(e) => {
              setters.incorporationDate.set(e);
            }}
            error={!!errors.incorporationDate}
            helperText={errors.incorporationDate ?? ''}
            disabled={disableForm}
          />
          <ContactInformationForm
            label="Primary address"
            data={fields.primaryAddress}
            editable={!disableForm}
            setters={setters.primaryAddress}
            errors={errors.primaryAddress}
          />
          <Input
            label="Description of the company's business operations"
            type="textarea"
            rows={3}
            value={fields.businessOperations}
            onChange={(e) => {
              setters.businessOperations.set(e.target.value);
            }}
            error={!!errors.businessOperations}
            helperText={
              errors.businessOperations ??
              "Please add information about the company's source of funds"
            }
            disabled={disableForm}
          />
          <Input
            label="What is the reason why your company wants to become a customer and use this specific product?"
            type="textarea"
            rows={3}
            value={fields.reasonForUse}
            onChange={(e) => {
              setters.reasonForUse.set(e.target.value);
            }}
            error={!!errors.reasonForUse}
            helperText={
              errors.reasonForUse ??
              'Please add information about the reason your company wants to become a customer and use this specific product'
            }
            disabled={disableForm}
          />
          <Input
            label="Payment reference"
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            value={fields.paymentReference ?? ''}
            onChange={(e) => {
              setters.paymentReference.set(e.target.value);
            }}
            helperText={errors.paymentReference}
            error={!!errors.paymentReference}
            disabled={disableForm}
          />
          <Input
            label="LEI number"
            value={fields.leiNumber ?? ''}
            onChange={(e) => {
              setters.leiNumber.set(
                e.target.value === '' ? null : e.target.value
              );
            }}
            error={!!errors.leiNumber}
            helperText={
              errors.leiNumber ??
              'If you have a LEI number, please provide it here.'
            }
            disabled={disableForm}
          />
          <Input
            label="IBAN"
            value={fields.iban}
            onChange={(e) => {
              setters.iban.set(e.target.value);
            }}
            helperText={errors.iban}
            error={!!errors.iban}
            disabled={disableForm}
          />

          <Input
            label="BIC"
            value={fields.bic}
            onChange={(e) => {
              setters.bic.set(e.target.value);
            }}
            helperText={errors.bic}
            error={!!errors.bic}
            disabled={disableForm}
          />
          <ListItem
            css={[styles.listItemBorder]}
            title="Please check if your business has any connection with or ties to the USA."
            icon={<Checkbox checked={fields.usaConnection} />}
            onClick={() => {
              setters.usaConnection.set(!fields.usaConnection);
            }}
            disabled={disableForm}
          />
          <Button
            text="Submit"
            onClick={submit}
            loading={loading}
            disabled={disableForm}
          />
        </div>
      </div>
      {!!value && (
        <Snackbar
          open={true}
          message={'KYB updated successfully'}
          variant="success"
        />
      )}
    </div>
  );
};
const useStyles = createStyles(({ sizing, spacing, palette }) => ({
  container: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    maxWidth: sizing.large,
    marginTop: spacing.xxxs,
    backgroundColor: palette.white,
    padding: spacing.s,
  },
  listItemBorder: {
    borderRadius: spacing.xxxs,
    background: palette.white,
    border: `1px solid ${palette.grey.x200}`,
    ':hover': {
      borderColor: palette.grey.x600,
    },
  },
}));
